import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatIconModule } from "@angular/material/icon";
import {
  EnrichmentPriceItem,
  ShelfRef,
} from "@app/shared/models/enrichment-detail.model";
import { isFlatEnrichmentModel } from "@app/shared/mappers/enrichment-detail.adapter";
import { EnrichmentDataService } from "../../../services/enrichment-data.service";
import { formatFigures } from "../../../utils/price-amount.util";
import { PriceEntry } from "../../../utils/price-entry.util";
import { EnrichmentPriceShelf } from "../enrichment-pricing/enrichment-price-shelf/enrichment-price-shelf";
import { ActivePriceStore } from "../enrichment-edit-package/active-price.store";
import { EnrichmentPriceDirtyStore } from "../enrichment-edit-package/enrichment-price-dirty.store";
import { SectionDirtyStore } from "../enrichment-edit-package/section-dirty.store";

export interface CopySource {
  readonly entry: PriceEntry;
  readonly gain: number;
}

function amountOf(entry: PriceEntry): string {
  return formatFigures(entry.figures);
}

function shelvesToAdd(
  bound: readonly ShelfRef[],
  incoming: readonly ShelfRef[],
): ShelfRef[] {
  const seen = new Set(bound.map((s) => s.id));
  return incoming.filter((s) => !seen.has(s.id));
}

function sameShelfIds(a: readonly ShelfRef[], b: readonly ShelfRef[]): boolean {
  return a.length === b.length && a.every((s, i) => s.id === b[i]?.id);
}

@Component({
  selector: "app-enrichment-shelf",
  standalone: true,
  imports: [MatIconModule, EnrichmentPriceShelf],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./enrichment-shelf.html",
  styleUrls: ["./enrichment-shelf.scss"],
})
export class EnrichmentShelf {
  private readonly data = inject(EnrichmentDataService);
  protected readonly active = inject(ActivePriceStore);
  private readonly priceDirty = inject(EnrichmentPriceDirtyStore);
  private readonly sectionDirty = inject(SectionDirtyStore);

  private readonly model = toSignal(this.data.enrichment$, {
    initialValue: null,
  });

  private readonly prices = computed<EnrichmentPriceItem[]>(() => {
    const m = this.model();
    return m && isFlatEnrichmentModel(m) ? (m.__detail.prices ?? []) : [];
  });

  private readonly channelSignal = toSignal(this.data.channel$, {
    initialValue: "" as ReturnType<EnrichmentDataService["getChannel"]>,
  });
  protected readonly channel = computed<string>(
    () => this.channelSignal() || "",
  );

  protected readonly boundShelves = computed<ShelfRef[]>(
    () => this.active.price()?.shelves ?? [],
    { equal: sameShelfIds },
  );

  protected readonly amountOf = amountOf;

  protected readonly copySources = computed<readonly CopySource[]>(() => {
    const shelvesOf = new Map(
      this.prices().map((p) => [p.id, p.shelves ?? []]),
    );
    const bound = this.boundShelves();
    return this.active
      .entries()
      .filter((entry) => entry.id !== this.active.popId())
      .map((entry) => ({
        entry,
        gain: shelvesToAdd(bound, shelvesOf.get(entry.id) ?? []).length,
      }))
      .filter((source) => source.gain > 0);
  });

  onPriceShelfAdd(shelf: ShelfRef): void {
    this.onPriceShelvesAdd([shelf]);
  }

  onPriceShelfRemove(shelfId: string): void {
    this.onPriceShelvesRemove([shelfId]);
  }

  onPriceShelvesAdd(shelves: readonly ShelfRef[]): void {
    this.updatePriceShelves((list) => [
      ...list,
      ...shelvesToAdd(list, shelves),
    ]);
  }

  onPriceShelvesRemove(ids: readonly string[]): void {
    const drop = new Set(ids);
    this.updatePriceShelves((list) => list.filter((s) => !drop.has(s.id)));
  }

  copyShelvesFrom(sourceId: string): void {
    if (sourceId === this.active.popId()) return;
    const source = this.prices().find((p) => p.id === sourceId);
    if (source) this.onPriceShelvesAdd(source.shelves ?? []);
  }

  private updatePriceShelves(patch: (shelves: ShelfRef[]) => ShelfRef[]): void {
    const priceId = this.active.popId();
    const m = this.model();
    if (!priceId || !m || !isFlatEnrichmentModel(m)) return;
    const draft = structuredClone(m);
    const price = draft.__detail.prices?.find((p) => p.id === priceId);
    if (!price) return;
    price.shelves = patch([...(price.shelves ?? [])]);
    this.data.setEnrichment(draft);
    this.priceDirty.markDirty(priceId);
    this.sectionDirty.markTabDirty("category");
  }
}
